import { useEffect } from 'react';
import { DATA_ROOM_HEARTBEAT_INTERVAL_MS } from 'shared';

import { BackendRoute } from '../../../config/index';
import { ApiService } from '../../../services/index';

/**
 * Tells the server the visitor still has this data room in front of them — 20.1.0 §8.
 *
 * It reports presence and nothing else: no duration, no start, no end, not even a count. How much
 * a beat is worth, which session it lands in and whether it counts at all are decided on the
 * server from its own clock, so this cannot draw itself time by beating faster or by beating after
 * the tab went away.
 *
 * The clock is the tab's focus. It stops the moment focus is lost rather than at the next tick,
 * which is what makes a pause a pause, and it beats at once on the way back in so the gap the
 * server sees is the length of the pause and not of the pause plus an interval.
 */
export const useDataRoomPresence = (dataRoomId: null | string): void => {
  useEffect(() => {
    if (!dataRoomId) return;

    let timer: null | ReturnType<typeof setInterval> = null;

    const stop = (): void => {
      if (!timer) return;

      clearInterval(timer);
      timer = null;
    };

    const beat = (): void => {
      void ApiService.post({
        endpoint: BackendRoute.MY_DATA_ROOMS,
        routePath: [dataRoomId, 'heartbeat'],
      }).catch(() => {
        // A room that has been closed or a grant that has been withdrawn answers 403, and the page
        // the visitor is looking at will deal with that. Beating at a shut door would not.
        stop();
      });
    };

    const start = (): void => {
      if (timer || document.visibilityState !== 'visible') return;

      beat();
      timer = setInterval(beat, DATA_ROOM_HEARTBEAT_INTERVAL_MS);
    };

    const onVisibilityChange = (): void =>
      document.visibilityState === 'visible' ? start() : stop();

    start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', start);
    window.addEventListener('blur', stop);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', start);
      window.removeEventListener('blur', stop);
    };
  }, [dataRoomId]);
};
